/**
 * Bringing a recipe in (#1286).
 *
 * ⛔ THE CONFIRM STEP IS THE FEATURE. The parser guesses which ingredients are
 * the meal and which are added to a plate, and that judgement is NOT IN A
 * RECIPE — "1 lb chicken thighs" cannot tell you whether this is a chicken
 * dish or a rice dish somebody adds chicken to. Get it wrong and the meal
 * quietly excludes half the household. So every guess is shown as a guess and
 * a person taps before anything is saved.
 *
 * ⚠️ Paste is the first-class path. Photographs only work on PRINTED text —
 * measured, not assumed: a printed page came back near perfect and a
 * handwritten one gave 142 characters of noise. The screen says so rather than
 * letting somebody retake the same photo four times.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { twoPlates, type ParsedRecipe, type ParsedIngredient, type RecipeStep } from "../api/twoPlates";
import { ApiError } from "../lib/api";
import { MealMedia } from "../components/MealMedia";

const AISLES = ["produce", "meat", "fish", "dairy", "refrigerated", "frozen", "bakery", "pantry"];

type Row = ParsedIngredient & { group: string | null; role: "base" | "fork"; aisle: string };

export default function Import() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: ["food-groups"], queryFn: twoPlates.foodGroups });

  const [text, setText] = useState("");
  const [draft, setDraft] = useState<ParsedRecipe | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [steps, setSteps] = useState<RecipeStep[]>([]);
  const [name, setName] = useState("");
  const [slowCooker, setSlowCooker] = useState(false);
  const [sideIds, setSideIds] = useState<number[]>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const sides = useQuery({ queryKey: ["library-sides"], queryFn: twoPlates.librarySides });

  // Editing an existing one, or adding a new one. Same screen either way
  // (#1337) — a second editor would drift from this within a month.
  const { mealId } = useParams();
  const editingId = mealId ? Number(mealId) : null;

  const existing = useQuery({
    queryKey: ["recipe", editingId],
    queryFn: () => twoPlates.loadRecipe(editingId!),
    enabled: !!editingId,
  });

  useEffect(() => {
    const r = existing.data?.recipe;
    if (!r) return;
    setName(r.name);
    setSlowCooker(!!r.slowCooker);
    setSteps(r.steps ?? []);
    setSideIds(r.sideIds ?? []);
    setRows(
      r.ingredients.map((i) => ({
        raw: i.item, quantity: null, item: i.item,
        suggestedGroup: i.group, suggestedRole: i.role,
        group: i.group, role: i.role, aisle: i.aisle,
      })),
    );
    setDraft({ title: r.name, servings: null, activeMinutes: r.activeMinutes ?? null,
      ingredients: [], steps: [], leftovers: [] });
  }, [existing.data]);

  const parse = useMutation({
    mutationFn: (body: { text?: string; imageBase64?: string }) => twoPlates.parseRecipe(body),
    onSuccess: (res) => {
      setDraft(res.draft);
      setName(res.draft.title);
      setWarning(res.warning);
      setRows(
        res.draft.ingredients.map((i) => ({
          ...i,
          group: i.suggestedGroup,
          role: i.suggestedRole,
          aisle: aisleFor(i.suggestedGroup),
        })),
      );
      setSteps(res.draft.steps.map((text) => ({ text, role: "base" as const })));
    },
  });

  const payload = () => ({
    name: name.trim(),
    activeMinutes: draft?.activeMinutes ?? 30,
    slowCooker,
    reheats: true,
    ingredients: rows.map((r) => ({ item: r.item, aisle: r.aisle, group: r.group, role: r.role })),
    steps,
    sideIds,
  });

  /**
   * A recipe you just added can take a photo straight away.
   *
   * ⛔ IT USED TO BOUNCE TO THE DECK THE INSTANT IT SAVED. The only screen with
   * a media control is the end of the cook flow, so the only way to put a
   * picture on a recipe you had just typed out was to go and cook it. Somebody
   * copying their grandmother's card has the photo in their hand NOW.
   *
   * ⚠️ Create only. An edit came from somewhere and should go back there.
   */
  const [savedMeal, setSavedMeal] = useState<{ id: number; name: string } | null>(null);

  const commit = useMutation({
    mutationFn: () =>
      editingId ? twoPlates.updateRecipe(editingId, payload()) : twoPlates.commitRecipe(payload()),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["deck"] });
      queryClient.invalidateQueries({ queryKey: ["recipe", editingId] });
      if (editingId || !res?.meal?.id) {
        navigate("/deck");
        return;
      }
      setSavedMeal(res.meal);
    },
  });

  async function onPhoto(file: File) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    parse.mutate({ imageBase64: base64 });
  }

  /* ── saved: the one moment the photo is in their hand ───────────── */
  if (savedMeal) {
    return (
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Added.</h1>
        <p className="text-muted mt-2 break-words text-sm">
          {savedMeal.name} is in your meals, and the deck can offer it.
        </p>

        {/* Same control as the end of the cook flow — a recipe you added is
            not a second-class one. */}
        <MealMedia mealId={savedMeal.id} />

        <button
          onClick={() => navigate("/deck")}
          className="bg-plate-b text-ground tap mt-8 w-full rounded-lg font-semibold"
        >
          Done
        </button>
        <button
          onClick={() => { setSavedMeal(null); setDraft(null); setText(""); }}
          className="text-muted mt-4 text-sm underline underline-offset-4"
        >
          Add another
        </button>
      </div>
    );
  }

  /* ── step one: get the words ────────────────────────────────────── */
  if (!draft) {
    return (
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Add a recipe</h1>
        <p className="text-muted mt-2 text-sm">
          Paste it from anywhere, or photograph a printed page.
        </p>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={"Weeknight Chicken and Rice\n\nINGREDIENTS\n2 cups jasmine rice\n1 lb chicken thighs\n…"}
          className="bg-surface border-line mt-5 w-full rounded-lg border px-3 py-3 text-base outline-none focus:border-plate-b"
        />

        <button
          onClick={() => parse.mutate({ text })}
          disabled={!text.trim() || parse.isPending}
          className="bg-plate-b text-ground tap mt-3 w-full rounded-lg font-semibold disabled:opacity-40"
        >
          {parse.isPending ? "Reading…" : "Read it"}
        </button>

        <button
          onClick={() => {
            // Straight to the editor. Making somebody type a recipe into a
            // textarea so a regex can take it apart again is absurd when they
            // already know what it is.
            setDraft({ title: "", servings: null, activeMinutes: 30, ingredients: [], steps: [], leftovers: [] });
            setRows([blankRow()]);
          }}
          className="border-line tap mt-3 w-full rounded-lg border font-medium"
        >
          Or write one from scratch
        </button>

        <div className="border-line mt-6 rounded-xl border p-4">
          <label htmlFor="photo" className="text-muted text-xs uppercase tracking-wide">
            Or photograph a page
          </label>
          <input
            id="photo"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPhoto(f); }}
            className="mt-2 block w-full text-sm"
          />
          {/* Said before the photo, not after four failed attempts. */}
          <p className="text-muted mt-2 text-xs">
            Printed pages only. Handwriting does not survive being read by a
            machine — for a handwritten card, type it above instead.
          </p>
        </div>

        {parse.isError && (
          <p role="alert" className="text-warn mt-4 text-sm">
            {parse.error instanceof ApiError ? parse.error.message : "Could not read that."}
          </p>
        )}
      </div>
    );
  }

  /* ── step two: the confirm that is the whole point ──────────────── */
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">
        {editingId ? name || "Edit this recipe" : "Which of these is the meal?"}
      </h1>
      <p className="text-muted mt-2 text-sm">
        Everything marked <span className="text-ink">the meal</span> is what everybody eats — refuse
        one and the dinner is gone. Everything marked <span className="text-plate-a">added</span> goes
        on one plate, so refusing it costs nothing.
      </p>

      {warning && (
        <p className="text-warn border-warn mt-4 rounded-lg border p-3 text-sm">{warning}</p>
      )}

      <label className="text-muted mt-6 block text-xs uppercase tracking-wide" htmlFor="rname">
        Call it
      </label>
      <input
        id="rname"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-surface border-line tap mt-2 w-full rounded-lg border px-4 text-base outline-none focus:border-plate-b"
      />

      <ul className="mt-6 flex flex-col gap-3">
        {rows.map((row, i) => (
          <li key={i} className="border-line rounded-xl border p-3">
            <input
              value={row.item}
              onChange={(e) => setRows((p) => p.map((r, j) => (j === i ? { ...r, item: e.target.value } : r)))}
              placeholder="What to buy"
              aria-label={"Ingredient " + (i + 1)}
              className="bg-surface border-line w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-plate-b"
            />
            {row.quantity && <p className="text-muted mt-1 text-xs">{row.quantity}</p>}

            <div className="mt-2 flex gap-2">
              {(["base", "fork"] as const).map((role) => (
                <button
                  key={role}
                  onClick={() => setRows((p) => p.map((r, j) => (j === i ? { ...r, role } : r)))}
                  aria-pressed={row.role === role}
                  className="border-line flex-1 rounded-lg border py-2 text-xs font-medium"
                  style={
                    row.role === role
                      ? {
                          borderColor: role === "fork" ? "var(--color-plate-a)" : "var(--color-plate-b)",
                          color: role === "fork" ? "var(--color-plate-a)" : "var(--color-plate-b)",
                        }
                      : undefined
                  }
                >
                  {role === "base" ? "The meal" : "Added to a plate"}
                </button>
              ))}
            </div>

            <div className="mt-2 flex gap-2">
              <select
                value={row.group ?? ""}
                onChange={(e) =>
                  setRows((p) => p.map((r, j) => (j === i ? { ...r, group: e.target.value || null } : r)))
                }
                aria-label={`Food group for ${row.item}`}
                className="bg-surface border-line flex-1 rounded-lg border px-2 py-2 text-xs"
              >
                <option value="">no group</option>
                {(groups.data?.groups ?? []).map((g) => (
                  <option key={g.key} value={g.key}>{g.name}</option>
                ))}
              </select>
              <select
                value={row.aisle}
                onChange={(e) => setRows((p) => p.map((r, j) => (j === i ? { ...r, aisle: e.target.value } : r)))}
                aria-label={`Aisle for ${row.item}`}
                className="bg-surface border-line flex-1 rounded-lg border px-2 py-2 text-xs"
              >
                {AISLES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button
                onClick={() => setRows((p) => p.filter((_, j) => j !== i))}
                aria-label={`Remove ${row.item}`}
                className="text-muted px-2 text-sm"
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ul>

      <label className="mt-6 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={slowCooker} onChange={(e) => setSlowCooker(e.target.checked)} />
        It is a slow cooker
      </label>
      {slowCooker && (
        <p className="text-muted mt-1 text-xs">
          Then anything added goes on at the table — you cannot take a portion out
          of a pot the meat cooked in all day.
        </p>
      )}

      <button
        onClick={() => setRows((p) => [...p, blankRow()])}
        className="text-plate-b mt-3 text-sm underline underline-offset-4"
      >
        Add an ingredient
      </button>

      <section className="mt-8">
        <h2 className="text-muted text-xs uppercase tracking-wide">Serve it with</h2>
        <p className="text-muted mt-1 text-xs">
          Without a side this is a main with no veg, and the plan will under-buy
          produce for it every week.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(sides.data?.sides ?? []).map((sd) => {
            const on = sideIds.includes(sd.id);
            return (
              <button
                key={sd.id}
                onClick={() => setSideIds((p) => (on ? p.filter((x) => x !== sd.id) : [...p, sd.id]))}
                aria-pressed={on}
                className="border-line rounded-lg border px-3 py-2 text-xs"
                style={on ? { borderColor: "var(--color-plate-b)", color: "var(--color-plate-b)" } : undefined}
              >
                {sd.name}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-muted text-xs uppercase tracking-wide">How to cook it</h2>
        <p className="text-muted mt-1 text-xs">
          Mark the step where the pot becomes two plates, and the step that adds
          each thing. Without them it cannot be cooked two ways.
        </p>

        <ul className="mt-3 flex flex-col gap-2.5">
          {steps.map((st, i) => (
            <li key={i} className="border-line rounded-xl border p-3">
              <textarea
                value={st.text}
                onChange={(e) => setSteps((p) => p.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                rows={2}
                aria-label={"Step " + (i + 1)}
                className="bg-surface border-line w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-plate-b"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(["base", "pivot", "fork"] as const).map((role) => (
                  <button
                    key={role}
                    onClick={() => setSteps((p) => p.map((x, j) => (j === i ? { ...x, role } : x)))}
                    aria-pressed={st.role === role}
                    className="border-line rounded-lg border px-2.5 py-1.5 text-[11px] font-medium"
                    style={st.role === role ? { borderColor: roleColour(role), color: roleColour(role) } : undefined}
                  >
                    {role === "base" ? "Everybody" : role === "pivot" ? "It splits here" : "Only for some"}
                  </button>
                ))}
                {st.role === "fork" && (
                  <select
                    value={st.group ?? ""}
                    onChange={(e) => setSteps((p) => p.map((x, j) => (j === i ? { ...x, group: e.target.value || null } : x)))}
                    aria-label={"Which addition step " + (i + 1) + " is for"}
                    className="bg-surface border-line rounded-lg border px-2 py-1.5 text-[11px]"
                  >
                    <option value="">which one?</option>
                    {rows.filter((r) => r.role === "fork" && r.group).map((r) => (
                      <option key={r.group as string} value={r.group as string}>{r.item}</option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => setSteps((p) => p.filter((_, j) => j !== i))}
                  aria-label={"Remove step " + (i + 1)}
                  className="text-muted px-2 text-sm"
                >
                  x
                </button>
              </div>
            </li>
          ))}
        </ul>

        <button
          onClick={() => setSteps((p) => [...p, { text: "", role: "base" }])}
          className="text-plate-b mt-3 text-sm underline underline-offset-4"
        >
          Add a step
        </button>
      </section>

      <div className="mt-6 flex gap-3">
        <button
          onClick={() => { setDraft(null); setRows([]); }}
          className="border-line tap flex-1 rounded-lg border font-medium"
        >
          Start over
        </button>
        <button
          onClick={() => commit.mutate()}
          disabled={!name.trim() || rows.length === 0 || commit.isPending}
          className="bg-plate-b text-ground tap flex-[2] rounded-lg font-semibold disabled:opacity-40"
        >
          {commit.isPending ? "Saving…" : editingId ? "Save changes" : "Add to my meals"}
        </button>
      </div>

      <p className="text-muted mt-3 text-xs">
        Only your household sees this. It joins your deck, not the shared library.
      </p>

      {commit.isError && (
        // The uncookable refusal is an INSTRUCTION, not a breakage: "mark the
        // step where the pot becomes two plates". It reads like one.
        <p role="alert" className="text-warn mt-3 text-sm">
          {commit.error instanceof ApiError ? commit.error.message : "Could not save it."}
        </p>
      )}

      {editingId && (
        <button
          onClick={() => {
            if (confirm("Stop offering this recipe? Any plan already built from it keeps working.")) {
              void twoPlates.removeRecipe(editingId).then(() => navigate("/deck"));
            }
          }}
          className="text-muted mt-6 text-sm underline underline-offset-4"
        >
          Stop offering this recipe
        </button>
      )}
    </div>
  );
}

function blankRow(): Row {
  return {
    raw: "", quantity: null, item: "",
    suggestedGroup: null, suggestedRole: "base",
    group: null, role: "base", aisle: "pantry",
  };
}

function roleColour(role: "base" | "pivot" | "fork"): string {
  if (role === "pivot") return "var(--color-warn)";
  if (role === "fork") return "var(--color-plate-a)";
  return "var(--color-plate-b)";
}

/** A sensible default aisle, still changeable on the row. */
function aisleFor(group: string | null): string {
  switch (group) {
    case "beef": case "chicken": case "pork": return "meat";
    case "fish": return "fish";
    case "cheese": case "cream": return "dairy";
    case "greens": case "peppers": case "tomato": case "fruit": case "potato": case "mushroom":
      return "produce";
    case "tofu": return "refrigerated";
    default: return "pantry";
  }
}
